import { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import {
  Asset,
  DOCUMENT_SELFIE,
  SecurityDto,
  MimeType,
  DOCUMENT_FACIAL,
  DOCUMENT_BACK,
  DOCUMENT_FRONT,
  SseAssetsTypes,
} from '../states';
import { useData } from './DataProvider';

type IProps = {
  children: ReactNode;
};

type ContextProps = {
  setWorkflow(value: boolean): void;
  workflow: boolean;
  setSelfie(value: string): void;
  selfie: string;
  setDocumentSides(value: SecurityDto): void;
  documentSides: SecurityDto;
  setFacialCapture(value: string): void;
  facialCapture: string;
  setAntispoofResult(value: Record<string, unknown>): void;
  antispoofResult: Record<string, unknown>;
  opreationAssets: Asset[];
};

const Ctx = createContext<ContextProps>({
  setWorkflow: () => {},
  workflow: false,
  setAntispoofResult: () => {},
  antispoofResult: {},
  setSelfie: () => {},
  selfie: '',
  setDocumentSides: () => {},
  documentSides: {
    frontImage: '',
    backImage: '',
  },
  setFacialCapture: () => {},
  facialCapture: '',
  opreationAssets: [],
});

export const OperationProvider = ({ children }: IProps) => {
  const [selfie, setSelfie] = useState<string>('');
  const [documentSides, setDocumentSides] = useState<SecurityDto>({
    frontImage: '',
    backImage: '',
  });
  const [facialCapture, setFacialCapture] = useState<string>('');
  const [antispoofResult, setAntispoofResult] = useState<
    Record<string, unknown>
  >({});
  const [workflow, setWorkflow] = useState<boolean>(false);
  const { facialAuthenticationState } = useData();

  const facialAssets = useMemo(() => {
    if (facialAuthenticationState?.data) {
      const validationData = facialAuthenticationState?.data?.links?.assets;
      const selfieElement = validationData?.find(
        (item) => item.type === SseAssetsTypes.SELFIE
      );
      const portraitElement = validationData?.find(
        (item) => item.type === SseAssetsTypes.ID_PORTRAIT
      );
      return { selfieElement, portraitElement };
    }
  }, [facialAuthenticationState.data]);

  const opreationAssets: Asset[] = useMemo(() => {
    return [
      {
        type: DOCUMENT_SELFIE,
        url: facialAssets?.selfieElement?.url || selfie,
        contentType: MimeType.jpeg,
        source: 'demo-backend',
      },
      {
        type: DOCUMENT_FACIAL,
        url: facialAssets?.portraitElement?.url || facialCapture,
        contentType: MimeType.jpeg,
        source: 'demo-backend',
      },
      {
        type: DOCUMENT_FRONT,
        url: documentSides.frontImage,
        contentType: MimeType.jpeg,
        source: 'demo-backend',
      },
      {
        type: DOCUMENT_BACK,
        url: documentSides.backImage,
        contentType: MimeType.jpeg,
        source: 'demo-backend',
      },
    ];
  }, [selfie, documentSides, facialCapture, facialAssets]);

  return (
    <Ctx.Provider
      value={{
        setSelfie,
        setDocumentSides,
        setFacialCapture,
        setAntispoofResult,
        setWorkflow,
        antispoofResult,
        selfie,
        documentSides,
        facialCapture,
        opreationAssets,
        workflow,
      }}
    >
      {children}
    </Ctx.Provider>
  );
};
export const useOperation = () => useContext(Ctx);
